<script setup>
import { TabGroup, TabList, Tab, TabPanels, TabPanel } from "@headlessui/vue";
import { ref } from "vue";
import { buttonColorMapper } from "@/helpers/colorHelper";
import { pushGAEvent } from "@/analyticsHelper";
import ControlPanelIcon from "./ControlPanelIcon";

const props = defineProps({
    keyNotesData: {
        type: Array,
        required: true,
    },
});

const selectedTab = ref(0);
const hasManualInput = ref(false);

const milliSeconds = 10000;

const loopThroughUsps = () => {
    setTimeout(() => {
        if (!hasManualInput.value && props.keyNotesData) {
            const maxLength = props.keyNotesData.length - 1;

            if (selectedTab.value <= maxLength) {
                selectedTab.value += 1;
            } else {
                selectedTab.value = 0;
            }
        }

        loopThroughUsps();
    }, milliSeconds);
};

setTimeout(() => {
    loopThroughUsps();
}, milliSeconds);
</script>

<template>
    <TabGroup :selected-index="selectedTab" @mouseover="hasManualInput = true" @mouseleave="hasManualInput = false">
        <div class="grid grid-cols-1 lg:grid-cols-2 lg:gap-12">
            <TabList class="col-span-1">
                <div
                    class="flex grid-cols-2 gap-3 overflow-x-scroll pb-5 -pr-5 lg:grid lg:gap-5 lg:overflow-visible lg:pb-0"
                >
                    <Tab
                        v-for="(keyNote, index) in keyNotesData"
                        :id="`tab_usp_${index}`"
                        v-slot="{ selected }"
                        :key="index"
                        class="flex-shrink-0"
                    >
                        <button
                            class="flex h-full w-full items-center px-5 py-3 shadow transition duration-300 ease-in-out space-x-3 xl:py-5 focus:ring-2 focus:ring-teal-600 active:ring-2 active:ring-teal-600"
                            :class="
                                (`hover:bg-${keyNote.key_note_button_color.value} border-${keyNote.key_note_button_color.value}`,
                                [
                                    selected
                                        ? `text-white bg-${keyNote.key_note_button_color.value} border border-${keyNote.key_note_button_color.value}`
                                        : `border bg-white border-gray-300 text-${keyNote.key_note_button_color.value}`,
                                ])
                            "
                            @click="
                                pushGAEvent('homepage-usp-click', {
                                    usp_index: index,
                                    usp_title: keyNote.key_note_item_title,
                                })
                            "
                        >
                            <i
                                class="text-lg xl:text-xl 2xl:text-2xl fa-light"
                                :class="`fa-${keyNote.key_note_item_icon.value}`"
                            ></i>
                            <span
                                class="block flex-shrink-0 text-left text-xs font-semibold lg:flex-shrink xl:text-sm 2xl:text-base"
                                v-text="keyNote.key_note_item_title"
                            />
                        </button>
                    </Tab>
                </div>
            </TabList>
            <TabPanels class="col-span-1 md:pb-12 lg:pb-0">
                <TabPanel v-for="(keyNote, index) in keyNotesData" :key="index" class="max-w-lg">
                    <h3
                        class="mb-5 text-xl uppercase font-heading md:text-2xl lg:text-3xl xl:text-4xl"
                        :class="`text-${keyNote.key_note_item_title_color.key}`"
                        v-text="keyNote.key_note_item_title"
                    />
                    <p class="mb-10 prose" v-text="keyNote.key_note_item_text" />
                    <template v-if="keyNote.key_note_item_activate_button">
                        <a
                            :href="keyNote.key_note_item_button_link"
                            :title="keyNote.key_note_item_button_text"
                            :class="buttonColorMapper(keyNote.key_note_item_button_color.value)"
                            class="flex max-w-max flex-grow-0 items-center border-b px-5 py-2 font-bold transition duration-300 ease-in-out space-x-3 hover:cursor-pointer focus:ring-2 focus:ring-teal-600 active:ring-2 active:ring-teal-600"
                        >
                            <span class="md:text-lg" v-text="keyNote.key_note_item_button_text" />
                            <i
                                v-if="keyNote.key_note_item_icon"
                                class="fa-light"
                                :class="`fa-${keyNote.key_note_item_icon.value}`"
                            ></i>
                        </a>
                    </template>
                </TabPanel>
            </TabPanels>
        </div>
    </TabGroup>
</template>
